import React, { useState } from 'react';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { CircularProgress, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, MenuItem } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import MomentUtils from '@date-io/moment';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import ComponentSerialsField from './ComponentSerialsField';
import { fieldDateFormat, backendDateFormat } from '../../../constants';
import { checkApiStatus } from '../../../utils_v2';
import { updateQmeTrackerApi } from '../apiServices';
import { addLandingGearPositions } from '../helperFunc';
import { qmeStatusOptions, qmeTypeOptions, qmeProgressOptions } from '../';

const QmeTrackerCRU = ({ qmeTracker, users, toggleModalFn, getResponseBack }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState({});
  const [record, setRecord] = useState({
    ...qmeTracker,
    tracker_status: qmeTracker.tracker_status ? qmeTracker.tracker_status.id : '',
    assigned_to: qmeTracker.assigned_to || [],
    component_serial_numbers: qmeTracker.component_serial_numbers || [],
  });
  const onFieldChange = (keyParam, value) => {
    setRecord(prev => {
      let next = { ...prev, [keyParam]: typeof value === 'function' ? value(prev[keyParam]) : value };
      if (keyParam === 'qme_type' && value === 'landing_gear') {
        next.component_serial_numbers = addLandingGearPositions(prev.component_serial_numbers);
      }
      return next;
    });
    setError(prev => ({ ...prev, [keyParam]: '' }));
  };
  const updateQmeTrackerFn = () => {
    let validationInputs = {
      workscope: record.workscope && record.workscope.trim() ? '' : 'Please enter Workscope / Event',
      // assigned_to: record.assigned_to.length ? '' : 'Please select Assigned To',
      start_date: record.start_date ? '' : 'Please select Start Date',
      tracker_status: record.tracker_status ? '' : 'Please select Status',
      tracker_progress: qmeProgressOptions.includes(record.tracker_progress) ? '' : 'Please select Progress',
      target_date: record.start_date && record.target_date && moment(record.target_date).isBefore(moment(record.start_date)) ? 'Target date should be greater than start date' : '',
    };
    const componentErrors = record.component_serial_numbers.map(item => item.label && !(item.serial || '').trim() ? 'Please enter the serial number' : '');
    if (Object.keys(validationInputs).every((k) => validationInputs[k] === '') && componentErrors.every(item => item === '')) {
      setLoading(true);
      updateQmeTrackerApi(record.slug, {
        sysco_claim_number: record.sysco_claim_number,
        qme_type: record.qme_type,
        component_serial_numbers: record.component_serial_numbers.filter(item => (item.serial || '').trim()),
        mro_facility: record.mro_facility,
        workscope: record.workscope,
        assigned_to: record.assigned_to.map(user => user.id),
        start_date: record.start_date,
        target_date: record.target_date,
        tracker_status: record.tracker_status,
        tracker_progress: record.tracker_progress,
        next_action: record.next_action,
        vendor_remarks: record.vendor_remarks,
        client_remarks: record.client_remarks,
      })
        .then(response => {
          setLoading(false);
          if (checkApiStatus(response)) {
            enqueueSnackbar(response.data.message, { variant: 'success', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
            getResponseBack();
          } else {
            enqueueSnackbar(response?.data?.message || 'Unable to update', { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
          }
        });
    } else {
      enqueueSnackbar('Please enter the mandatory fields, highlighted in red', { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
      setError({ ...validationInputs, component_serial_numbers: componentErrors });
    }
  };
  const textField = (keyParam, label, props = {}) => (
    <TextField
      id={keyParam}
      label={label}
      fullWidth
      margin="normal"
      variant="outlined"
      InputLabelProps={{ shrink: true }}
      value={record[keyParam] || ''}
      error={!!error[keyParam]}
      helperText={error[keyParam] || ''}
      onChange={(e) => onFieldChange(keyParam, e.target.value)}
      {...props}
    />
  );
  const dateField = (keyParam, label, required) => (
    <MuiPickersUtilsProvider utils={MomentUtils}>
      <KeyboardDatePicker
        margin="normal"
        id={keyParam}
        label={label}
        required={required}
        format={fieldDateFormat}
        fullWidth
        clearable={true}
        InputLabelProps={{ shrink: true }}
        inputVariant="outlined"
        value={record[keyParam] || null}
        error={!!error[keyParam]}
        helperText={error[keyParam] || ''}
        onChange={(data) => onFieldChange(keyParam, data ? moment(data).format(backendDateFormat) : null)}
      />
    </MuiPickersUtilsProvider>
  );
  return (
    <Dialog open={true} onClose={toggleModalFn} maxWidth={'md'} className='project-management-modal'>
      <DialogTitle id="scroll-dialog-title">Edit Tracker Update</DialogTitle>
      <DialogContent dividers={true}>
        <Grid container spacing={1}>
          <Grid item xs={12} md={6}>{textField('project_number', 'Project / Claim Reference', { disabled: true, value: `#${record.project_number} - ${record.name}` })}</Grid>
          <Grid item xs={12} md={6}>{textField('sysco_claim_number', 'SYSCO Claim Number')}</Grid>
          <Grid item xs={12} md={4}>{textField('lessee', 'Lessee', { disabled: true, value: record.lessee ? record.lessee.name : '' })}</Grid>
          <Grid item xs={12} md={4}>{textField('msn', 'MSN', { disabled: true, value: (record.msn || []).join(', ') })}</Grid>
          <Grid item xs={12} md={4}>{textField('location', 'Location', { disabled: true })}</Grid>
          <Grid item xs={12} md={6}>
            {textField('qme_type', 'QME Type', { select: true, disabled: true, children: qmeTypeOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>) })}
          </Grid>
          <Grid item xs={12} md={6}>{textField('mro_facility', 'MRO Facility')}</Grid>
          <Grid item xs={12}>
            <ComponentSerialsField
              value={record.component_serial_numbers}
              error={error.component_serial_numbers}
              onChange={(value) => onFieldChange('component_serial_numbers', value)}
            />
          </Grid>
          <Grid item xs={12}>{textField('workscope', 'Workscope / Event', { required: true })}</Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              multiple
              disabled
              options={users}
              getOptionLabel={option => option.name}
              getOptionSelected={(option, value) => option.id === value.id}
              id="assigned_to"
              value={record.assigned_to}
              onChange={(e, value) => onFieldChange('assigned_to', value)}
              renderInput={params => <TextField {...params} label="Assigned To" margin="normal" fullWidth InputLabelProps={{ shrink: true }} variant="outlined" />}
            />
          </Grid>
          <Grid item xs={12} md={3}>{dateField('start_date', 'Start Date', true)}</Grid>
          <Grid item xs={12} md={3}>{dateField('target_date', 'Target Date', false)}</Grid>
          <Grid item xs={12} md={4}>
            {textField('tracker_status', 'Status', { select: true, required: true, children: qmeStatusOptions.map(option => <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>) })}
          </Grid>
          <Grid item xs={12} md={4}>
            {textField('tracker_progress', 'Progress (%)', { select: true, required: true, children: qmeProgressOptions.map(value => <MenuItem key={value} value={value}>{`${value}%`}</MenuItem>) })}
          </Grid>
          <Grid item xs={12} md={4}>{textField('next_action', 'Next Action / Due')}</Grid>
          <Grid item xs={12}>{textField('vendor_remarks', 'Vendor Remarks', { multiline: true, rows: 3 })}</Grid>
          <Grid item xs={12}>{textField('client_remarks', 'CSL Remarks', { multiline: true, rows: 3 })}</Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <ul className='list-inline'>
          <li className='list-inline-item'>
            <Button disabled={isLoading} onClick={toggleModalFn} size="small" color="primary" variant="outlined">Cancel</Button>
          </li>
          <li className='list-inline-item'>
            <Button disabled={isLoading} onClick={updateQmeTrackerFn} size="small" color="primary" variant="contained">
              {isLoading ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          </li>
        </ul>
      </DialogActions>
    </Dialog>
  )
}
export default QmeTrackerCRU;
